/**
 * TiptapEditor — Controlled rich-text editor component.
 *
 * Props:
 * - value: JSONContent  — the current editor content as Tiptap JSON
 * - onChange: (value: JSONContent) => void — called on every content change
 * - placeholder?: string — optional placeholder text
 *
 * Features:
 * - StarterKit (headings, bold, italic, lists, etc.)
 * - Placeholder extension
 * - Link extension (with href prompt on click)
 * - Toolbar: Bold, Italic, H1, H2, H3, BulletList, OrderedList, Link
 *
 * Sync pattern:
 * - useRef tracks last externally supplied value to prevent infinite loops
 * - useEffect updates editor content when value changes externally (e.g., locale switch)
 * - immediatelyRender: false — prevents SSR hydration mismatch
 */

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import type { JSONContent } from '@tiptap/react'
import { useEffect, useRef } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface TiptapEditorProps {
  value: JSONContent
  onChange: (value: JSONContent) => void
  placeholder?: string
}

// ── Toolbar button ─────────────────────────────────────────────────────────────

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        'px-2 py-1 text-sm rounded border transition-colors',
        active
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

// ── Editor ────────────────────────────────────────────────────────────────────

export function TiptapEditor({ value, onChange, placeholder }: TiptapEditorProps) {
  // Track last externally provided value to avoid update loops
  const lastExternalValue = useRef<string>(JSON.stringify(value))

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          class: 'text-blue-600 underline',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder ?? 'Write content here...',
      }),
    ],
    content: value,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON()
      // Track the new value so our effect doesn't re-set it
      lastExternalValue.current = JSON.stringify(json)
      onChange(json)
    },
  })

  // Sync external value when locale switches or value resets
  useEffect(() => {
    if (!editor) return
    const serialized = JSON.stringify(value)
    if (serialized !== lastExternalValue.current) {
      lastExternalValue.current = serialized
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  if (!editor) return null

  // ── Link handler ────────────────────────────────────────────────────────────

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Enter link URL:', previousUrl ?? '')
    if (url === null) return // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-300">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <em>I</em>
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          H1
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          H2
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          H3
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          &#8226; List
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Ordered List"
        >
          1. List
        </ToolbarButton>

        <div className="w-px bg-gray-300 mx-1 self-stretch" />

        <ToolbarButton
          onClick={setLink}
          active={editor.isActive('link')}
          title="Add Link"
        >
          Link
        </ToolbarButton>

        {editor.isActive('link') && (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Remove Link"
          >
            Unlink
          </ToolbarButton>
        )}
      </div>

      {/* Content area */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-3 min-h-32 focus-within:outline-none"
      />
    </div>
  )
}
